import { UserIcon as User, HeartIcon as Heart, SearchIcon as Search, XIcon as X } from "@/components/icons/HeaderIcons";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import PrefetchLink from "@/components/PrefetchLink";
// LCP-critical: use a SMALL bundled variant as `src` (240w ≈ 7 KB) instead of
// the 85 KB original. The raw `?url` import pulls in the full 85 KB even when
// srcset picks a smaller variant.
// @ts-expect-error – vite-imagetools query import
import zooiLogo from "@/assets/zooi-logo-transparent.webp?w=240&format=webp";
// @ts-expect-error – vite-imagetools query import
import zooiLogoSrcsetWebp from "@/assets/zooi-logo-transparent.webp?w=120;240;480&format=webp&as=srcset";
// @ts-expect-error – vite-imagetools query import
import zooiLogoSrcsetAvif from "@/assets/zooi-logo-transparent.webp?w=120;240;480&format=avif&as=srcset";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

const SearchAutocomplete = lazyWithRetry(() => import("@/components/SearchAutocomplete"));
const MobileMenu = lazyWithRetry(() => import("@/components/MobileMenu"));
const CartDrawer = lazyWithRetry(() => import("@/components/CartDrawer"));
const TrustBadgesMobile = lazyWithRetry(() => import("@/components/TrustBadgesMobile"));
const HeaderTrustUSPs = lazyWithRetry(() => import("@/components/HeaderTrustUSPs"));
const UserDropdown = lazyWithRetry(() => import("@/components/UserDropdown"));

const Header = () => {
  const { user, signOut, isAuthReady } = useAuth();
  const [wishlistCount, setWishlistCount] = useState(0);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => {
    if (!isAuthReady) return;

    if (user) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => fetchWishlistCount());
      } else {
        setTimeout(fetchWishlistCount, 200);
      }
    } else {
      setWishlistCount(0);
    }
  }, [user, isAuthReady]);

  const fetchWishlistCount = async () => {
    if (!user) return;

    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { count, error } = await supabase
        .from("wishlist")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      if (error) throw error;
      setWishlistCount(count || 0);
    } catch (error) {
      console.error("Error fetching wishlist count:", error);
    }
  };

  return (
    <header className="w-full bg-background border-b border-border/40">
      <div className="container flex items-center justify-between px-2 md:px-6 h-[100px] md:h-[110px]">
        <div className="flex items-center gap-0 shrink-0 md:gap-2">
          <Suspense fallback={<div className="w-10 h-10 md:w-10 md:h-10" />}><MobileMenu /></Suspense>
          <PrefetchLink to="/" className="flex items-center justify-center -ml-1 md:ml-0">
            <picture>
              <source type="image/avif" srcSet={zooiLogoSrcsetAvif} sizes="(max-width: 767px) 160px, 240px" />
              <source type="image/webp" srcSet={zooiLogoSrcsetWebp} sizes="(max-width: 767px) 160px, 240px" />
              <img
                src={zooiLogo}
                alt="Zooi - Din djuraffär online"
                width={240}
                height={120}
                className="h-[72px] w-[160px] md:h-[240px] md:w-[240px] object-contain mt-[16px] md:mt-[40px]"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </picture>
          </PrefetchLink>

        </div>

        <div className="hidden md:flex flex-1 items-center max-w-2xl mx-6">
          <div className="w-full">
            <Suspense fallback={<div className="relative flex items-center bg-secondary/40 border border-border/60 rounded-full px-5 py-3 h-[46px]" />}>
              <SearchAutocomplete />
            </Suspense>
          </div>
        </div>

        <div className="flex items-center gap-0 md:gap-2 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 md:hidden rounded-full text-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
            onClick={() => setMobileSearchOpen(true)}
            aria-label="Sök produkter"
          >
            <Search className="h-[22px] w-[22px]" strokeWidth={2} aria-hidden="true" />
          </Button>

          {!isAuthReady ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 md:h-11 md:w-11 rounded-full text-foreground"
              aria-label="Laddar konto"
              disabled
            >
              <User className="h-[22px] w-[22px] md:h-7 md:w-7" strokeWidth={2} aria-hidden="true" />
            </Button>
          ) : user ? (
            <Suspense fallback={
              <Button variant="ghost" size="icon" className="h-9 w-9 md:h-11 md:w-11 rounded-full" aria-label="Min profil">
                <User className="h-[22px] w-[22px] md:h-7 md:w-7" strokeWidth={2} />
              </Button>
            }>
              <UserDropdown onSignOut={signOut} />
            </Suspense>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 md:h-11 md:w-11 rounded-full text-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
              asChild
              aria-label="Logga in"
            >
              <Link to="/logga-in">
                <User className="h-[22px] w-[22px] md:h-7 md:w-7" strokeWidth={2} aria-hidden="true" />
              </Link>
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 md:h-11 md:w-11 rounded-full text-foreground hover:text-foreground hover:bg-secondary/50 transition-colors relative"
            asChild
            aria-label={`Önskelista ${wishlistCount > 0 ? `(${wishlistCount} produkter)` : ""}`}
          >
            <Link to="/onskelista">
              <Heart className="h-[22px] w-[22px] md:h-7 md:w-7" strokeWidth={2} aria-hidden="true" />
              {wishlistCount > 0 && (
                <Badge className="absolute -top-0.5 -right-0.5 h-4 min-w-4 md:h-5 md:min-w-5 p-0 flex items-center justify-center text-[9px] md:text-[10px] bg-primary text-primary-foreground border-2 border-background rounded-full">
                  {wishlistCount}
                </Badge>
              )}
            </Link>
          </Button>

          <Suspense fallback={<div className="w-9 h-9 md:w-11 md:h-11" />}><CartDrawer /></Suspense>
        </div>
      </div>

      <div className="hidden md:block border-t border-border/20">
        <div className="container px-4 md:px-6 py-2 flex justify-center">
          <Suspense fallback={<div className="hidden md:flex items-center justify-center gap-5 h-[28px]" />}><HeaderTrustUSPs /></Suspense>
        </div>
      </div>

      {mobileSearchOpen && (
        <div className="fixed inset-0 z-[200] bg-background md:hidden">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/40">
            <div className="flex-1">
              <Suspense fallback={<div className="h-[44px] bg-secondary/40 border border-border/60 rounded-full" />}>
                <SearchAutocomplete isMobile autoFocus onClose={() => setMobileSearchOpen(false)} />
              </Suspense>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-full"
              onClick={() => setMobileSearchOpen(false)}
              aria-label="Stäng sök"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
